/**
 * Reusable collapsible section component with support for nested levels
 */
import React, { useState } from "react";
import {
  View,
  Text,
  Pressable,
  StyleSheet,
  Animated,
  LayoutAnimation,
  Platform,
  UIManager,
  useWindowDimensions,
  StyleProp,
  ViewStyle,
} from "react-native";
import global from "../../../../styles/globalStyles";

// Enable LayoutAnimation on Android
if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

interface CollapsibleSectionProps {
  title: string;
  children?:
    | React.ReactNode
    | ((args: { isVisible: boolean }) => React.ReactNode);
  defaultCollapsed?: boolean;
  level?: number; // 0 = year, 1 = month, 2 = day
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  children,
  defaultCollapsed = true,
  level = 0,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { width } = useWindowDimensions();
  const compact = width < 380;

  const INDENT_PER_LEVEL = compact ? 8 : 12;
  const HEADER_HPAD = compact ? 10 : 14;
  const CONTENT_LPAD = compact ? 8 : 12;

  const toggleCollapsed = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsCollapsed(!isCollapsed);
  };

  const getHeaderStyle = () => {
    const base = [styles.headerBase, { paddingHorizontal: HEADER_HPAD }];
    switch (level) {
      case 0:
        return [...base, styles.yearHeader];
      case 1:
        return [...base, styles.monthHeader];
      case 2:
        return [...base, styles.dayHeader];
      default:
        return [...base, styles.yearHeader];
    }
  };

  const getTextStyle = () => {
    switch (level) {
      case 0: // Year
        return [global.text, global.fontSize18, global.fontWeight700];
      case 1: // Month
        return [global.text, global.fontSize16, global.fontWeight600];
      case 2: // Day
        return [global.text, global.fontSize14, global.fontWeight500];
      default:
        return [global.text, global.fontSize18, global.fontWeight700];
    }
  };

  const getContainerStyle = (): StyleProp<ViewStyle> => [
    styles.container,
    level > 0 && {
      marginLeft: level * INDENT_PER_LEVEL,
      marginVertical: 4,
    },
  ];

  // const renderChildren = () => {
  //   if (typeof children === "function") {
  //     const render = children as (args: {
  //       isVisible: boolean;
  //     }) => React.ReactNode;
  //     return render({ isVisible: !isCollapsed });
  //   }
  //   return children;
  // };

  return (
    <View style={getContainerStyle()}>
      <Pressable onPress={toggleCollapsed} style={getHeaderStyle()}>
        <Text style={styles.chevron}>{isCollapsed ? "▶" : "▼"}</Text>
        <Text style={getTextStyle()}>{title}</Text>
      </Pressable>

      {!isCollapsed && (
        <View style={[styles.content, { paddingLeft: CONTENT_LPAD }]}>
          {typeof children === "function"
            ? children({ isVisible: !isCollapsed })
            : children}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 4,
  },
  headerBase: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 10,
    // paddingHorizontal: 16,
    borderRadius: 8,
  },
  yearHeader: {
    backgroundColor: "#e3f2fd",
    borderLeftWidth: 4,
    borderLeftColor: "#1976d2",
  },
  monthHeader: {
    backgroundColor: "#f3e5f5",
    borderLeftWidth: 3,
    borderLeftColor: "#7b1fa2",
  },
  dayHeader: {
    backgroundColor: "#e8f5e8",
    borderLeftWidth: 2,
    borderLeftColor: "#388e3c",
  },
  chevron: {
    fontSize: 12,
    marginRight: 12,
    color: "#666",
    width: 16,
  },
  content: {
    paddingLeft: 16,
    paddingTop: 6,
  },
});
